import { mediaRepo } from '../../db/mediaRepo';
import type { ContentRow } from '../../types/content';
import type { MediaRenderer, RenderContext, RenderSlot } from './types';
import { applyVideoVolume, cssObjectFit, showOnly } from './util';

// Plays a downloaded video blob. Ported from VideoFrame (minus Android-specific
// recovery): buffer in prepare(), reveal on the first rendered frame.
export const videoRenderer: MediaRenderer = {
  type: 'video',
  needsMedia: true,

  async prepare(slot: RenderSlot, content: ContentRow, ctx: RenderContext): Promise<boolean> {
    const url = await mediaRepo.objectUrl(content.fileName);
    if (!url) return false;
    slot.objectUrl = url;

    showOnly(slot, 'video');
    slot.video.style.objectFit = cssObjectFit(content.resizeMode);
    slot.video.loop = false;
    slot.video.playsInline = true;
    slot.video.preload = 'auto';
    applyVideoVolume(slot.video, ctx.getVolume(), ctx.isAudioUnlocked());

    return await new Promise<boolean>((resolve) => {
      let settled = false;
      const done = (ok: boolean) => {
        if (settled) return;
        settled = true;
        slot.video.oncanplaythrough = null;
        slot.video.onloadeddata = null;
        slot.video.onerror = null;
        resolve(ok);
      };
      slot.video.onloadeddata = () => done(true);
      slot.video.oncanplaythrough = () => done(true);
      slot.video.onerror = () => {
        // A cached blob that fails to decode is corrupted (e.g. an unclean
        // power-off truncated the IndexedDB write). Drop it so the next sync
        // re-downloads a good copy instead of failing on it forever.
        void mediaRepo.delete(content.fileName);
        done(false);
      };
      slot.video.src = url;
      slot.video.load();
      // Safety: treat as ready even if events are slow; activate() has its own
      // first-frame watchdog.
      setTimeout(() => done(true), ctx.firstFrameTimeoutMs);
    });
  },

  async activate(slot: RenderSlot, content: ContentRow, ctx: RenderContext): Promise<boolean> {
    return await new Promise<boolean>((resolve) => {
      let settled = false;
      let watchdog: ReturnType<typeof setTimeout> | null = null;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        slot.video.onplaying = null;
        slot.video.ontimeupdate = null;
        if (watchdog) clearTimeout(watchdog);
        // Only when the element reports a real media error (not merely a slow
        // first frame): the cached blob is corrupt, drop it for re-download.
        if (!ok && slot.video.error) void mediaRepo.delete(content.fileName);
        resolve(ok);
      };

      const onProgress = () => finish(true);
      slot.video.onplaying = onProgress;
      slot.video.ontimeupdate = () => {
        if (slot.video.currentTime > 0) onProgress();
      };

      applyVideoVolume(slot.video, ctx.getVolume(), ctx.isAudioUnlocked());
      try {
        slot.video.currentTime = 0;
      } catch {
        /* noop */
      }
      const playPromise = slot.video.play();
      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay may reject if not muted; retry muted.
          slot.video.muted = true;
          slot.video.play().catch(() => finish(false));
        });
      }

      // First-frame watchdog: if nothing renders, give up on this item.
      watchdog = setTimeout(() => finish(false), ctx.firstFrameTimeoutMs);
    });
  },

  deactivate(slot: RenderSlot): void {
    try {
      slot.video.pause();
    } catch {
      /* noop */
    }
  },
};
